/**
 * The generator request, as a pure function of the answers the wizard collected.
 *
 * WHY THIS IS ITS OWN MODULE (2026-07-31): the create flow is being reordered and its review step is
 * becoming editable (docs/design/create-flow.md). The hard requirement on that work is that image
 * quality cannot change — every detail collected today must still be collected, structured the same
 * way, and handed to the image generator unchanged. While this assembly lived as a closure inside
 * CreatePage that requirement was unprovable: nothing could exercise it without driving the whole
 * component. Extracted, it is pinned by generateRequest.guard.test.js, and any edit that drops a
 * field, renames one, or changes an empty-value rule fails immediately instead of silently degrading
 * a prompt.
 *
 * The behaviour here is a verbatim move of create.js's former `buildRequest`. It is deliberately not
 * an improvement of it: the guard's whole value is that this output is identical to what production
 * sent before the flow work started.
 */

// v2: mirror the backend's noun-anchored classifier so "Create one like this" carries the change-type.
// A visual/artwork noun → 'artwork' (edit the picture); everything else — names, greetings, wording, or
// empty — → 'text' (words-only; the backend re-bakes the cover text over the EXACT source art, no image
// model). The backend re-classifies authoritatively; this is submitted for parity/telemetry.
export const ARTWORK_EDIT_PATTERN =
  /\b(art\s?work|artistic|picture|image|imagery|photo(?:graph)?|scene|scenery|background|backdrop|colou?rs?|palette|style|illustration|drawing|painting|graphics?|landscape|flowers?|roses?|floral|petals?|border|frame|pattern|wreath|trees?|leaves|leaf|birds?|butterfly|butterflies|flag|redraw|repaint|recolou?r)\b/i;

export const classifyChange = (text) => {
  const t = (text || '').trim();
  if (!t) return 'text';
  return ARTWORK_EDIT_PATTERN.test(t) ? 'artwork' : 'text';
};

// The feelings that reach the prompt: the taxonomy picks plus the shopper's own words, in that order.
// Used both by the payload and by the suggestion fields, so the two can never disagree about what the
// generator was told.
export const allFeelings = (feelings, ownFeeling) => [
  ...(feelings || []),
  ...((ownFeeling || '').trim() ? [(ownFeeling || '').trim()] : []),
];

export function buildGenerateRequest({
  obituaryId,
  deceasedDetails,
  sessionId,
  feelings,
  ownFeeling,
  styleId,
  imageryDescription,
  coverText,
  photoKey,
  photoUsage,
  photoNote,
  referenceCardId,
  changeInstruction,
  notifyWhenReady,
  notifyEmail,
}) {
  return {
    obituaryId,
    deceasedDetails,
    // Anon identity for generation metering (cost control). Same stable id used by "My creations";
    // lets the server count generations per browser. In OBSERVE mode (default) this changes nothing.
    sessionId,
    feeling: allFeelings(feelings, ownFeeling),
    styleId,
    // Imagery feeds the ART prompt (visual only); cover words are the type layer and are NEVER sent
    // to the image model — kept separate here so the two can never be merged (the prod garble bug).
    imageryDescription: (imageryDescription || '').trim() || undefined,
    coverText: (coverText || '').trim() || undefined,
    photoKeys: photoKey ? [photoKey] : [],
    // photoUsage + photoNote only matter when a photo is attached (BR-CREATE-09).
    photoUsage: photoKey ? photoUsage : undefined,
    photoNote: photoKey && (photoNote || '').trim() ? (photoNote || '').trim() : undefined,
    // "Create one like this": the source card (visual reference) + the free-text change. The backend
    // resolves referenceCardId → the card's cover image, sent alongside any user photo (recreate-with-
    // changes). Empty change = "make one just like it".
    referenceCardId: referenceCardId || undefined,
    changeInstruction: (changeInstruction || '').trim() || undefined,
    changeType: referenceCardId ? classifyChange(changeInstruction) : undefined,
    // Browse-away opt-in (Phase B): only sent when explicitly checked WITH an address. Absent → the
    // server stores no email and sends no notification.
    notifyEmail: notifyWhenReady && (notifyEmail || '').trim() ? (notifyEmail || '').trim() : undefined,
    // HOTFIX 2026-06-30: no client count override — the server's DEFAULT_OPTION_COUNT (1) is the
    // single source of truth. Serial Ultra calls for >1 option time out API Gateway (29s).
  };
}
